import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";

import lockfile from "proper-lockfile";

import { CliError, EXIT_INPUT, EXIT_RUNTIME } from "../common.js";

export interface WorkingCopyMetadata {
  schemaVersion: 1;
  projectGroupNo: string;
  apiBaseUrl: string;
  baseRevision: number;
  baseContentHash: string;
}

export interface WorkingCopyPaths {
  scriptPath: string;
  stateDir: string;
  metadataPath: string;
  lockPath: string;
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === "object") {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) result[key] = canonicalize(source[key]);
    return result;
  }
  return value;
}

export function semanticScriptHash(script: unknown): string {
  const canonical = JSON.stringify(canonicalize(script));
  return `sha256:${crypto.createHash("sha256").update(canonical).digest("hex")}`;
}

export function localFileFingerprint(filePath: string): string | null {
  if (!fs.existsSync(filePath)) return null;
  return `sha256:${crypto.createHash("sha256").update(fs.readFileSync(filePath)).digest("hex")}`;
}

export function assertLocalFileFingerprint(filePath: string, expected: string | null): void {
  const current = localFileFingerprint(filePath);
  if (current === expected) return;
  throw new CliError("The local script changed while the command was running.", {
          exitCode: EXIT_INPUT,
          errorCode: "LOCAL_SCRIPT_CHANGED",
          hint: "Read the latest local script and retry the command.",
          data: { actual: [`loadedFingerprint=${expected ?? "<missing>"}`, `currentFingerprint=${current ?? "<missing>"}`, filePath] },
        });
}

export function workingCopyPaths(rawScriptPath: string): WorkingCopyPaths {
  const scriptPath = path.resolve(rawScriptPath);
  const stateDir = path.join(path.dirname(scriptPath), ".scriptctl");
  const name = path.basename(scriptPath);
  return {
    scriptPath,
    stateDir,
    metadataPath: path.join(stateDir, `${name}.working-copy.json`),
    lockPath: path.join(stateDir, name),
  };
}

export async function withScriptFileLock<T>(rawScriptPath: string, operation: () => Promise<T>): Promise<T> {
  const paths = workingCopyPaths(rawScriptPath);
  fs.mkdirSync(paths.stateDir, { recursive: true });
  let release: (() => Promise<void>) | null = null;
  try {
    release = await lockfile.lock(paths.lockPath, {
      realpath: false,
      stale: 120_000,
      update: 10_000,
      retries: { retries: 60, factor: 1, minTimeout: 500, maxTimeout: 500 },
    });
  } catch (error) {
    throw new CliError("Timed out waiting for the script file lock.", {
            exitCode: EXIT_RUNTIME,
            errorCode: "WORKING_COPY_LOCK_TIMEOUT",
            hint: "Wait for the other scriptctl command to finish, then retry.",
            data: { actual: [paths.scriptPath, error instanceof Error ? error.message : String(error)] },
          });
  }
  try {
    return await operation();
  } finally {
    await release();
  }
}

export function atomicWriteJson(filePath: string, value: unknown): void {
  atomicWriteText(filePath, `${JSON.stringify(value, null, 2)}\n`);
}

export function atomicWriteText(filePath: string, content: string): void {
  const dir = path.dirname(filePath);
  fs.mkdirSync(dir, { recursive: true });
  const tempPath = path.join(dir, `.${path.basename(filePath)}.${process.pid}.${crypto.randomUUID()}.tmp`);
  const descriptor = fs.openSync(tempPath, "wx");
  try {
    fs.writeFileSync(descriptor, content, "utf-8");
    fs.fsyncSync(descriptor);
  } finally {
    fs.closeSync(descriptor);
  }
  fs.renameSync(tempPath, filePath);
}

export function readWorkingCopyMetadata(rawScriptPath: string): WorkingCopyMetadata | null {
  const metadataPath = workingCopyPaths(rawScriptPath).metadataPath;
  if (!fs.existsSync(metadataPath)) return null;
  let value: unknown;
  try {
    value = JSON.parse(fs.readFileSync(metadataPath, "utf-8"));
  } catch (error) {
    throw invalidMetadata(metadataPath, error instanceof Error ? error.message : String(error));
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw invalidMetadata(metadataPath, "metadata root must be an object");
  }
  const record = value as Record<string, unknown>;
  if (record.schemaVersion !== 1
      || typeof record.projectGroupNo !== "string" || record.projectGroupNo.length === 0
      || typeof record.apiBaseUrl !== "string" || record.apiBaseUrl.length === 0
      || !Number.isInteger(record.baseRevision) || Number(record.baseRevision) < 0
      || typeof record.baseContentHash !== "string" || !record.baseContentHash.startsWith("sha256:")) {
    throw invalidMetadata(metadataPath, "required fields are missing or invalid");
  }
  return {
    schemaVersion: 1,
    projectGroupNo: record.projectGroupNo,
    apiBaseUrl: record.apiBaseUrl,
    baseRevision: Number(record.baseRevision),
    baseContentHash: record.baseContentHash,
  };
}

export function writeWorkingCopyMetadata(rawScriptPath: string, metadata: WorkingCopyMetadata): void {
  atomicWriteJson(workingCopyPaths(rawScriptPath).metadataPath, metadata);
}

function invalidMetadata(metadataPath: string, reason: string): CliError {
  return new CliError("Working copy metadata is invalid.", {
           exitCode: EXIT_INPUT,
           errorCode: "WORKING_COPY_METADATA_INVALID",
           hint: "Run `scriptctl checkout --discard-local` to recreate the working copy metadata.",
           data: { expected: ["valid scriptctl working-copy metadata"], actual: [metadataPath, reason] },
         });
}

export function assertScriptUnchanged(scriptPath: string, expectedHash: string): void {
  let current: unknown;
  try {
    current = JSON.parse(fs.readFileSync(scriptPath, "utf-8"));
  } catch (error) {
    throw new CliError("The local script changed while the command was running.", {
            exitCode: EXIT_INPUT,
            errorCode: "LOCAL_SCRIPT_CHANGED",
            hint: "Read the latest local script and re-apply the operation.",
            data: { actual: [scriptPath, error instanceof Error ? error.message : String(error)] },
          });
  }
  const currentHash = semanticScriptHash(current);
  if (currentHash !== expectedHash) {
    throw new CliError("The local script changed while the command was running.", {
            exitCode: EXIT_INPUT,
            errorCode: "LOCAL_SCRIPT_CHANGED",
            hint: "Read the latest local script and re-apply the operation.",
            data: { actual: [`loadedHash=${expectedHash}`, `currentHash=${currentHash}`, scriptPath] },
          });
  }
}
